import { useDispatch } from "react-redux";
import { useCallback } from "react";
import axios from "axios";
import { socialLogin } from "../redux/slices/authSlice"; // same action you used for Google

const UseInstagramLogin = () => {
  const dispatch = useDispatch();

  // Step 1: Start Instagram Login - open OAuth URL in popup
  const loginInsta = () => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/frontend/instagram/login`,
          {
            appId: process.env.REACT_APP_INSTAGRAM_APP_ID || "937085468929020",
          },
        );

        const url = response?.data?.data;

        if (!url) {
          throw new Error("No authorization URL received from server");
        }

        // Open Instagram login in a popup to avoid mobile redirect hijacking
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          url,
          "InstagramLogin",
          `width=${width},height=${height},top=${top},left=${left}`,
        );

        // BroadcastChannel is used instead of postMessage because Instagram
        // nulls out window.opener during cross-origin OAuth redirects, making
        // postMessage unreachable. BroadcastChannel works same-origin across
        // all tabs/windows regardless of opener state.
        const bc = new BroadcastChannel("instagram-oauth");
        let popupTimer;

        bc.onmessage = async (event) => {
          bc.close();
          clearInterval(popupTimer);
          if (popup && !popup.closed) popup.close();

          const { token, userId, error, errorDescription } = event.data;

          if (error) {
            reject(
              new Error(
                `Instagram Auth Error: ${error} - ${errorDescription || "none"}`,
              ),
            );
            return;
          }

          if (token && userId) {
            try {
              console.log("Instagram callback popup - Processing login...");
              const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/frontend/instagram/user-info`,
                { access_token: token, user_id: userId },
              );
              const instaUser = res.data.data;

              const socialLoginData = {
                provider: "instagram",
                provider_id: instaUser.id,
                name: instaUser.username,
                instagram_access_token: token,
                email: instaUser.email || null,
                device_type: "web",
              };

              await dispatch(socialLogin(socialLoginData)).unwrap();
              resolve(socialLoginData);
            } catch (err) {
              console.error("Instagram popup callback error:", err);
              reject(err);
            }
          }
        };

        // Detect if user manually closed the popup before completing auth
        popupTimer = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(popupTimer);
            bc.close();
            reject(new Error("User closed the Instagram login window"));
          }
        }, 1000);
      } catch (err) {
        console.error("Instagram login error:", err);
        reject(err);
      }
    });
  };

  // Step 2: Handle callback (after Instagram redirects back to frontend via backend)
  const handleCallback = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const error = queryParams.get("error");
      const errorDescription = queryParams.get("error_description");
      const token = queryParams.get("token");
      const userId = queryParams.get("user_id");

      if (!error && !token) return; // No Instagram params

      // If we are inside a popup, send message to parent window and close
      if (window.opener && window.opener !== window) {
        window.opener.postMessage(
          {
            type: "INSTAGRAM_AUTH_MESSAGE",
            payload: { token, userId, error, errorDescription },
          },
          window.location.origin,
        );
        window.close();
        return;
      }

      // Fallback: If it was a full page redirect (not a popup)
      if (error) {
        console.error(
          "Instagram auth fallback error:",
          error,
          errorDescription,
        );
        alert(`Instagram Auth Error: ${error} - ${errorDescription || "none"}`);
        return;
      }

      if (token && userId) {
        console.log("Instagram callback fallback - Processing login...");
        const payload = { access_token: token, user_id: userId };

        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/frontend/instagram/user-info`,
          payload,
        );
        const instaUser = res.data.data;

        const socialLoginData = {
          provider: "instagram",
          provider_id: instaUser.id,
          name: instaUser.username,
          username: instaUser.username,
          instagram_access_token: token,
          email: instaUser.email || null,
          device_type: "web",
        };

        await dispatch(socialLogin(socialLoginData)).unwrap();
      }
    } catch (err) {
      console.error("Instagram callback error:", err);
    }
  }, [dispatch]);

  return { loginInsta, handleCallback };
};

export default UseInstagramLogin;
